import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useMemo } from "react";

import { parseDate } from "../../../../../helpers/date";
import { ISourceDataStatus } from "../../../../../types/baseRateFile";

const DATA_SET_LABELS: Record<keyof ISourceDataStatus, string> = {
  addendumB: "Addendum B",
  addendumAA: "Addendum AA",
  clfs: "CLFS",
  asp: "ASP",
  dmepos: "DMEPOS",
  pprvu: "MPFS (PPRRVU)",
  ipps: "IPPS Table 5 FR (MS-DRG Data)",
  ippsStdAmounts: "IPPS Tables 1A-1E (Standardized Amounts)",
  ippsImpact: "IPPS Impact File (Hospital Data)",
};

interface ITableRowData {
  id: string;
  dataSetKey: keyof ISourceDataStatus;
  dataSetLabel: string;
  year: number;
  lastUpdated: Date | null;
  count: number;
}

interface ISourceDataTableProps {
  sourceDataStatus: ISourceDataStatus | null;
}

function SourceDataTable({ sourceDataStatus }: ISourceDataTableProps) {
  const tableData = useMemo(() => {
    if (!sourceDataStatus) return [];

    const data: ITableRowData[] = [];

    (Object.keys(DATA_SET_LABELS) as (keyof ISourceDataStatus)[]).forEach(
      (key) => {
        const yearInfoArray = sourceDataStatus[key];

        if (yearInfoArray && yearInfoArray.length > 0) {
          // Sort by year descending (newest first)
          const sortedYearInfo = [...yearInfoArray].sort(
            (a, b) => b.year - a.year
          );

          sortedYearInfo.forEach((yearInfo) => {
            data.push({
              id: `${key}-${yearInfo.year}`,
              dataSetKey: key,
              dataSetLabel: DATA_SET_LABELS[key],
              year: yearInfo.year,
              lastUpdated: yearInfo.lastUpdated,
              count: yearInfo.count,
            });
          });
        }
      }
    );

    return data;
  }, [sourceDataStatus]);

  const dataSetBodyTemplate = (rowData: ITableRowData) => {
    return (
      <div className="flex gap-2 align-items-center">
        <i className="pi pi-file" style={{ color: "var(--primary-color)" }} />
        <span className="file-name">{rowData.dataSetLabel}</span>
      </div>
    );
  };

  const yearBodyTemplate = (rowData: ITableRowData) => {
    return rowData.year;
  };

  const lastUpdatedBodyTemplate = (rowData: ITableRowData) => {
    if (!rowData.lastUpdated) return "-";
    return parseDate(rowData.lastUpdated);
  };
  const countBodyTemplate = (rowData: ITableRowData) => {
    if (rowData.count === null) return "-";
    return rowData.count.toLocaleString();
  };

  if (tableData.length === 0) {
    return null;
  }

  return (
    <DataTable
      value={tableData}
      className="p-datatable-sm"
      emptyMessage="No data available"
      dataKey="id"
    >
      <Column
        field="dataSetLabel"
        header="Data Set"
        body={dataSetBodyTemplate}
        style={{ minWidth: "200px" }}
      />
      <Column
        field="year"
        header="Year"
        body={yearBodyTemplate}
        style={{ width: "100px" }}
      />
      <Column
        field="lastUpdated"
        header="Last Updated"
        body={lastUpdatedBodyTemplate}
        style={{ width: "150px" }}
      />
      <Column
        field="count"
        header="Rows Count"
        body={countBodyTemplate}
        style={{ width: "120px" }}
      />
    </DataTable>
  );
}

export default SourceDataTable;
